import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Button, Avatar, Menu, MenuItem } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { orange } from '@mui/material/colors';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import { useAuthMethod, useAuthUser } from '../../../../utility/AuthHooks';
import { Fonts } from 'shared/constants/AppEnums';
import { useNavigate } from 'react-router-dom';
import Api from '@api';
import axios from 'axios';
import { useAuthenticatedImage } from '@common/useAuthenticatedImage';
import ChangePassword from 'pages/account/MyProfile/ChangePassword';

const GLOBAL_ROLES = ['SUPERADMIN', 'TANENTADMIN'];

const roleDisplayMap = {
  ROSTERADMIN: 'Manager',
  TANENTADMIN: 'Tenant Admin',
  CORPORATEADMIN: 'Corporate Admin',
  EMPLOYEE: 'Employee'
};

const UserInfo = (props) => {
  const { color } = props;
  const { logout } = useAuthMethod();
  const { user } = useAuthUser();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [openChangePwd, setOpenChangePwd] = useState(false);
  const [profileImg, setProfileImg] = useState('');
  const [showSwitch, setShowSwitch] = useState(false);
  const [isPasswordEnabled, setIsPasswordEnabled] = useState(false);

  const userName = localStorage.getItem('contactPersonFirstName');
  const userList = user?.userList || {};

  const policyKey = useMemo(() => {
    const role = userList?.userRole;
    if (!role) return null;
    return GLOBAL_ROLES.includes(role) ? 'SYSTEM' : userList?.corporateId || null;
  }, [userList?.userRole, userList?.corporateId]);

  useEffect(() => {
    if (!policyKey) return;
    axios
      .get(`${Api.auth.getPasswordPolicy}/${policyKey}`)
      .then((res) => {
        if (res?.data?.status === 200) {
          setIsPasswordEnabled(res.data.data?.isPasswordEnabled === true);
        }
      })
      .catch(() => {});
  }, [policyKey]);

  // Use authenticated image hook to fetch image with vtt_user_signature header
  const authenticatedImageUrl = useAuthenticatedImage(profileImg);

  const getRoleName = useCallback(() => {
    const matchedRole = userList?.userRoles?.find(
      (el) => el.userRole === userList.userRole
    );
    return matchedRole?.userRoleName || '';
  }, [userList]);

  useEffect(() => {
    if (userList?.userRoleName === 'EMPLOYEE') {
      axios
        .get(`${Api.employee.list}/${userList.profileId}`)
        .then((res) => setProfileImg(res?.data?.data?.photo || ''))
        .catch(() => {});
    }
  }, [userList?.userRoleName, userList?.profileId]);

  useEffect(() => {
    const switchableRoles = [
      'ROSTERADMIN',
      'EMPLOYEE',
      'CORPORATEADMIN',
      'TANENTADMIN',
    ];
  
    const hasSwitchableRoles = userList?.userRoles?.some((role) =>
      switchableRoles.includes(role.userRole)
    );
  
    setShowSwitch(Boolean(hasSwitchableRoles));
  }, [userList?.userRoles]);

  const switchRole = (role) => {
    localStorage.setItem('switched', role);
    window.location.href = '/';
  };

  const getUserAvatar = () => {
    const name =
      userList?.contactName || userList?.userName || user?.email || '';
    return name.charAt(0).toUpperCase();
  };

  const handleOpenMenu = (event) => {
    props.setCollapsedFun(false);
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => setAnchorEl(null);
  const handleOpenDialog = () => setOpen(true);
  const handleCloseDialog = () => setOpen(false);
  const handleOpenChangePwd = () => { handleCloseMenu(); setOpenChangePwd(true); };
  const handleCloseChangePwd = () => setOpenChangePwd(false);

  const handleLogout = () => {
    const refreshToken = localStorage.getItem('refresh-token');
    if (user?.uid) {
      axios
        .post(
          `${Api.auth.logoutApi}/${
            user?.userList?.profileId || null
          }?refreshToken=${refreshToken}&userId=${user.uid}`
        )
        .then((res) => {
          if (res?.data?.status == '200') {
            logout();
          } else {
            toast.error('Something went wrong');
          }
        })
        .catch((err) => {
          console.error('err', err);
          toast.error('Something went wrong');
        });
    } else {
      logout();
    }
  };

  const renderRole = () => {
    if (user?.role === 'MANAGER') return 'Manager';
    if (user?.role === 'EMPLOYEE') return 'Employee';
    if (user?.role === 'TANENTADMIN') return userName || 'Super Admin';
    if (user?.role === 'SUPERADMIN') return 'eTravelMate Admin';
    if (userList?.userRoleName === 'EMPLOYEE') return getRoleName();
    return user?.role || 'N/A';
  };

  const renderSwitchOptions = () => {
    if (!showSwitch || userList?.userStatus === 'DEFAULT') return null;
  
    const { userRole: currentRole, userRoleName, userRoles = [] } = userList;

    
    return userRoles.map((role) => {
      const isEmployeeRestricted = userRoleName !== 'CORPORATEADMIN' && userRoleName !== 'TANENTADMIN' && role.userRole === 'EMPLOYEE';
      
      const isSameRole =
      role.userRole === currentRole ||
      (currentRole === 'MANAGER' && role.userRole === 'ROSTERADMIN');

      if (isEmployeeRestricted || isSameRole) return null;
  
      const targetRole =
        role.userRole === 'ROSTERADMIN' ? 'MANAGER' : role.userRole;
  
      const displayName = roleDisplayMap[role.userRoleName] ?? role.userRoleName;
  
      return (
        <MenuItem
          key={role.userRole}
          onClick={() => switchRole(targetRole)}
        >
          Switch To {displayName}
        </MenuItem>
      );
    });
  };

  return (
    <>
      <Box
        sx={{
          py: 3,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        className="user-info-view"
      >
        <Box sx={{ py: 0.5 }}>
          <Avatar
            sx={{
              height: 40,
              width: 40,
              fontSize: 24,
              backgroundColor: orange[500],
            }}
            src={authenticatedImageUrl || ''}
          >
            {!profileImg && getUserAvatar()}
          </Avatar>
        </Box>
        <Box
          sx={{
            width: { xs: 'calc(100% - 62px)', xl: 'calc(100% - 72px)' },
            ml: 4,
            color: color,
          }}
          className="user-info"
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                mb: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: 16,
                fontWeight: Fonts.MEDIUM,
                color: user?.userList?.theme?.fontColor || 'white',
              }}
              component="span"
            >
              {user?.userList?.userRole == 'CORPORATEADMIN'
                ? user?.userList?.contactName
                : user?.userList?.userName ?? 'Admin User '}
              {/* Please Check USER NAME HERE */}
            </Box>
            <Box
              sx={{
                ml: 3,
                color: user?.userList?.theme?.fontColor || 'white',
                display: 'flex',
              }}
            >
              <ExpandMoreIcon
                sx={{ color: user?.userList?.theme?.fontColor || 'white' }}
                onClick={handleOpenMenu}
              />
            </Box>
          </Box>
          <Box
            sx={{
              mt: -0.5,
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: user?.userList?.theme?.fontColor || 'white',
            }}
          >
            {renderRole()}
          </Box>
        </Box>
      </Box>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {userList?.userRole !== 'SUPERADMIN' &&
          [
            'CORPORATEADMIN',
            'MANAGER',
            'DRIVER',
            'EMPLOYEE',
            'VENDOR',
            'TANENTADMIN',
          ].includes(userList?.userRole) && (
            <MenuItem
              onClick={() => {
                handleCloseMenu();
                if (user?.userList?.userStatus == 'DEFAULT') {
                  navigate('/my-profile');
                } else {
                  navigate('/my-profile-update-request');
                }
              }}
            >
              My account
            </MenuItem>
          )}
        {(userList?.userRole !== 'EMPLOYEE' && userList?.userRoleName === 'EMPLOYEE') && (
          <MenuItem onClick={() => switchRole('EMPLOYEE')}>
            Switch To Employee
          </MenuItem>
        )}
        {renderSwitchOptions()}

        {isPasswordEnabled && (
          <MenuItem onClick={handleOpenChangePwd}>Change Password</MenuItem>
        )}
        <MenuItem onClick={handleOpenDialog}>Log Out</MenuItem>
      </Menu>

      <Dialog
        open={openChangePwd}
        onClose={handleCloseChangePwd}
        maxWidth={false}
        PaperProps={{ sx: { width: '560px', borderRadius: '10px' } }}
        BackdropProps={{ sx: { backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0,0,0,0.4)' } }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#f4f2f2',
            color: '#000000',
            py: 2,
            px: 3,
            fontSize: '17px',
            fontWeight: 600,
            position: 'relative',
          }}
        >
          Change Password
          <CloseIcon
            onClick={handleCloseChangePwd}
            style={{
              cursor: 'pointer',
              position: 'absolute',
              top: '16px',
              right: '16px',
              color: '#000000',
            }}
          />
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important', px: 3, pb: 3 }}>
          <ChangePassword onSuccess={handleCloseChangePwd} />
        </DialogContent>
      </Dialog>

      <Dialog
        className="yourClassName"
        open={open}
        onClose={handleCloseMenu}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle
          id="alert-dialog-title"
          style={{
            fontSize: '16px',
            background: '#e0e1e4',
            marginTop: '-2px',
            marginLeft: '-5px',
            paddingBottom: '7px',
          }}
        >
          {'Confirm Logout'}
        </DialogTitle>
        <DialogContent style={{ paddingTop: '14px', paddingBottom: '0px' }}>
          <DialogContentText
            id="alert-dialog-description"
            style={{ display: 'flex' }}
          >
            <img
              src="/assets/images/logout_.png"
              style={{ maxWidth: '45px', marginRight: '15px' }}
              alt="logout-icon"
            />
            {/* <LogoutIcon style={{ fontSize: '46px', marginRight: '15px' }}/> */}
            <p style={{ fontSize: '15px' }}>
              Are you sure, You want to Logout?
            </p>
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ paddingRight: '15px', paddingBottom: '15px' }}>
          {/* <Button id='btnMui123' variant="contained" style={{float:"left"}} >Yes </Button> */}
          {/* <Button id='btnMui123' variant="contained" >No </Button> */}
          <Button
            id="btnMui123"
            style={{
              background: '#0e5d7f',
              color: 'white',
              borderRadius: '20px',
              height: '30px',
            }}
            onClick={handleLogout}
          >
            Yes
          </Button>
          <Button
            id="btnMui123"
            style={{
              border: '1px solid #0e5d7f',
              color: '#0e5d7f',
              borderRadius: '20px',
              height: '30px',
            }}
            onClick={handleCloseDialog}
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserInfo;

UserInfo.defaultProps = {
  color: 'text.secondary',
};

UserInfo.propTypes = {
  color: PropTypes.string,
  setCollapsedFun: PropTypes.func,
};
